import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { BsSearch } from 'react-icons/bs';
import { debounce } from 'lodash';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import { Col, Row, Spinner } from 'react-bootstrap';
import SearchBox from 'components/general/SearchBox';
import ReactPagination from 'components/general/ReactPagination';
import { FETCH_LIVE_CHARGING_SESSION } from 'actions/liveChargingSession';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import { FiRefreshCw } from 'react-icons/fi';
import { getCustomerName } from 'components/common/utils';

// CSS for blink and fade/slide animations
const styles = `
  .blink-green {
    animation: blinkGreen 0.5s ease;
  }
  .blink-red {
    animation: blinkRed 0.5s ease;
  }
  @keyframes blinkGreen {
    0% { color: white; }
    50% { color: rgba(0, 255, 0, 0.3); }
    100% { color: white; }
  }
  @keyframes blinkRed {
    0% { color: white; }
    50% { color: rgba(255, 0, 0, 0.3); }
    100% { color: white; }
  }
  .fade-in {
    animation: fadeIn 0.5s ease-in;
  }
  .slide-out {
    animation: slideOut 0.2s ease-out forwards;
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(-100px); opacity: 0; }
  }
`;

const ChargingSessionForm = () => {
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);
  const [webSocketRef, setWebSocketRef] = useState(null);
  const [liveUpdates, setLiveUpdates] = useState({});
  const [completedSessions, setCompletedSessions] = useState(new Set());
  const [newSessions, setNewSessions] = useState(new Set());
  const [closingSessions, setClosingSessions] = useState(new Set());
  const [blinkingFields, setBlinkingFields] = useState({});
  const profileData = useSelector((state) => state.profile.userProfile);
  const allSession = useSelector((state) => state.liveChargingSession.liveChargingSessions);
  const isLoader = useSelector((state) => state.liveChargingSession.isLoading);
  const page = useSelector((state) => state.liveChargingSession.page);
  const totalData = useSelector((state) => state.liveChargingSession.total);
  const limit = useSelector((state) => state.liveChargingSession.limit);
  const totalPages = useSelector((state) => state.liveChargingSession.totalPages);
  const tenantId = _.get(profileData, 'tenant.id', '');

  const getChargingSession = useCallback(
    debounce((data = {}) => {
      const payload = {
        ...data,
        search: searchText || undefined,
        limit: limit,
      };
      dispatch({ type: FETCH_LIVE_CHARGING_SESSION.REQUEST, payload });
    }, 300),
    [searchText, isRealTimeActive, limit, dispatch]
  );

  useEffect(() => {
    if (!isRealTimeActive) {
      getChargingSession({ page: page });
    }
  }, [isRealTimeActive, searchText, getChargingSession]);

  const handleSearch = () => {
    getChargingSession({ page: 1 });
  };

  const handlePageClick = useCallback(
    (page) => {
      getChargingSession({ page: page.selected + 1 });
    },
    [getChargingSession]
  );

  useEffect(() => {
    if (isRealTimeActive) {
      const ws = new WebSocket(`${process.env.REACT_APP_LIVE_CHARGING_SESSION}?tenant=${tenantId}`);
      setWebSocketRef(ws);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (!data?.id) return;

        console.log("WebSocket data:", data);

        // Handle new booking: call getChargingSession API
        if (data.is_new_booking === true) {
          getChargingSession({ page: page });
          setNewSessions((prev) => new Set(prev).add(data.id));
          setTimeout(() => {
            setNewSessions((prev) => {
              const newSet = new Set(prev);
              newSet.delete(data.id);
              return newSet;
            });
          }, 500);
          return;
        }

        // Handle completed session
        if (data.status === 'completed') {
          setClosingSessions((prev) => new Set(prev).add(data.id));
          setTimeout(() => {
            setCompletedSessions((prev) => new Set(prev).add(data.id));
            setClosingSessions((prev) => {
              const newSet = new Set(prev);
              newSet.delete(data.id);
              return newSet;
            });
            getChargingSession({ page: page });
          }, 500);
          return;
        }

        // Handle existing session updates via WebSocket
        const isSessionPresent = allSession?.some((session) => session.id === data.id);
        if (!isSessionPresent) {
          // If session is not in allSession but not a new booking, skip further processing
          return;
        }

        setLiveUpdates((prev) => {
          const prevData = prev[data.id] || {};
          const fieldsToBlink = {};
          ['voltage', 'current', 'temperature', 'energy', 'current_soc'].forEach((field) => {
            const prevValue = _.get(prevData, `meterValue.${field}`, 0);
            const newValue = _.get(data, `meterValue.${field}`, 0);
            if (newValue > prevValue) {
              fieldsToBlink[`${data.id}-${field}`] = 'green';
            } else if (newValue < prevValue) {
              fieldsToBlink[`${data.id}-${field}`] = 'red';
            }
          });

          setTimeout(() => {
            setBlinkingFields((prev) => {
              const newBlinks = { ...prev };
              Object.keys(fieldsToBlink).forEach((key) => delete newBlinks[key]);
              return newBlinks;
            });
          }, 500);

          setBlinkingFields((prev) => ({ ...prev, ...fieldsToBlink }));

          return {
            ...prev,
            [data.id]: {
              ...prev[data.id],
              ...data,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      };

      ws.onerror = (err) => console.error('WebSocket error:', err);

      return () => {
        ws.close();
        setWebSocketRef(null);
        setLiveUpdates({});
        setCompletedSessions(new Set());
        setNewSessions(new Set());
        setClosingSessions(new Set());
        setBlinkingFields({});
      };
    } else if (webSocketRef) {
      webSocketRef.close();
      setWebSocketRef(null);
      setLiveUpdates({});
      setCompletedSessions(new Set());
      setNewSessions(new Set());
      setClosingSessions(new Set());
      setBlinkingFields({});
    }
  }, [isRealTimeActive, page, tenantId, allSession, getChargingSession]);

  const filteredSessions = allSession.filter((session) => !completedSessions.has(session.id));

  // New useEffect to handle page reset when no data is found
  useEffect(() => {
    if (_.isEmpty(filteredSessions) && page !== 1 && !isLoader) {
      getChargingSession({ page: 1 });
    }
  }, [filteredSessions, page, isLoader, getChargingSession]);

  return (
    <React.Fragment>
      <style>{styles}</style>
      <LayoutContainer>
        <AdminHeader title={t('header.liveChargingSessions')} />
        <div className="page-content-wrapper scrollable">
          <div className="liveChargingSession-management-page_main">
            <div className="liveChargingSession-list-main-block">
              <Card>
                <div className="liveChargingSession-list-block">
                  <div className="liveChargingSession-search-box">
                    <Row>
                      <Col xl={9} lg={9} md={9}>
                        <SearchBox
                          preIcon={<BsSearch />}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSearch();
                            }
                          }}
                        />
                      </Col>
                      <Col xl={2} lg={2} md={2}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Form.Check
                            type="switch"
                            id="realtime-switch"
                            label="Live"
                            style={{ marginTop: '10px' }}
                            checked={isRealTimeActive}
                            onChange={(e) => {
                              setIsRealTimeActive(e.target.checked);
                            }}
                          />
                          <FiRefreshCw
                            onClick={() => getChargingSession({ page: page })}
                            style={{ cursor: 'pointer', marginTop: '10px', marginLeft: '20px' }}
                            title="Refresh data"
                          />
                        </div>
                      </Col>
                    </Row>
                    <div className="liveChargingSession-list-table">
                      <div className="table-responsive">
                        <table className="record-list-table">
                          <thead>
                            <tr>
                              <th><span>{t('liveChargingForm.userName')}</span></th>
                              <th><span>{t('liveChargingForm.mobile')}</span></th>
                              <th><span>{t('liveChargingForm.connector')}</span></th>
                              <th><span>{t('liveChargingForm.connectorId')}</span></th>
                              <th><span>{t('liveChargingForm.rfid')}</span></th>
                              <th><span>{t('liveChargingForm.startSoC')}</span></th>
                              <th><span>{t('liveChargingForm.currentSoC')}</span></th>
                              <th><span>{t('liveChargingForm.transactionID')}</span></th>
                              <th><span>{t('liveChargingForm.voltage')}</span></th>
                              <th><span>{t('liveChargingForm.current')}</span></th>
                              <th><span>{t('liveChargingForm.temperature')}</span></th>
                              <th><span>{t('liveChargingForm.energy')}</span></th>
                              <th><span>{t('liveChargingForm.startTime')}</span></th>
                              <th><span>{t('liveChargingForm.Duration')}</span></th>
                              <th><span>{t('liveChargingForm.InitiatedFrom')}</span></th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoader ? (
                              <tr>
                                <td colSpan={15} className="border-0">
                                  <div className="loader--block">
                                    <Spinner as="span" animation="border" size="sm" role="status" />
                                    <span className="ps-2">{t('placeHolder.loading')}</span>
                                  </div>
                                </td>
                              </tr>
                            ) : _.isEmpty(filteredSessions) ? (
                              <tr>
                                <td colSpan={15} className="border-0">
                                  <div className="empty-data-block">{t('No data Found')}</div>
                                </td>
                              </tr>
                            ) : (
                              !(isLoader || _.isUndefined(isLoader)) &&
                              _.map(filteredSessions, (item, key) => {
                                const sessionId = _.get(item, 'id', '');
                                const updatedItem = liveUpdates[sessionId] ? { ...item, ...liveUpdates[sessionId] } : item;
                                const isNew = newSessions.has(sessionId);
                                const isClosing = closingSessions.has(sessionId);
                                return (
                                  <tr
                                    key={`country-${key}`}
                                    className={`${isNew ? 'fade-in' : ''} ${isClosing ? 'slide-out' : ''}`}
                                  >
                                    <td>{getCustomerName(updatedItem, '')}</td>
                                    <td>{_.get(updatedItem, 'customer_user_booked.phone', '')}</td>
                                    <td>{_.get(updatedItem, 'charger.charger_id', '')}</td>
                                    <td>{_.get(updatedItem, 'connectorId', '')}</td>
                                    <td>{_.get(updatedItem, 'idTag', '')}</td>
                                    <td>{_.get(updatedItem, 'StartSoC', '')}</td>
                                    <td className={blinkingFields[`${sessionId}-current_soc`] ? `blink-${blinkingFields[`${sessionId}-current_soc`]}` : ''}>
                                      {_.get(updatedItem, 'meterValue.current_soc', '')}
                                    </td>
                                    <td>{_.get(updatedItem, 'transaction_id', '')}</td>
                                    <td className={blinkingFields[`${sessionId}-voltage`] ? `blink-${blinkingFields[`${sessionId}-voltage`]}` : ''}>
                                      {_.get(updatedItem, 'meterValue.voltage', '')}
                                    </td>
                                    <td className={blinkingFields[`${sessionId}-current`] ? `blink-${blinkingFields[`${sessionId}-current`]}` : ''}>
                                      {_.get(updatedItem, 'meterValue.current', '')}
                                    </td>
                                    <td className={blinkingFields[`${sessionId}-temperature`] ? `blink-${blinkingFields[`${sessionId}-temperature`]}` : ''}>
                                      {_.get(updatedItem, 'meterValue.temperature', '')}
                                    </td>
                                    <td className={blinkingFields[`${sessionId}-energy`] ? `blink-${blinkingFields[`${sessionId}-energy`]}` : ''}>
                                      {_.get(updatedItem, 'meterValue.energy', '')}
                                    </td>
                                    <td>{moment(_.get(updatedItem, 'booking_start', '')).format('DD/MM/YYYY hh:mm:ss')}</td>
                                    <td>
                                      {(() => {
                                        const updatedAt = _.get(updatedItem, 'updatedAt', '');
                                        const bookingStart = _.get(updatedItem, 'booking_start', '');
                                        if (updatedAt && bookingStart) {
                                          const updatedAtDate = new Date(updatedAt);
                                          const bookingStartDate = new Date(bookingStart);
                                          if (!isNaN(updatedAtDate) && !isNaN(bookingStartDate)) {
                                            const diffInMs = Math.abs(updatedAtDate - bookingStartDate);
                                            const hours = Math.floor(diffInMs / (1000 * 60 * 60));
                                            const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
                                            const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);
                                            return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
                                          }
                                        }
                                        return '00h 00m 00s';
                                      })()}
                                    </td>
                                    <td>{_.get(updatedItem, 'initiatedFrom', '')}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {!_.isEmpty(filteredSessions) && (
                      <ReactPagination
                        currentPage={page}
                        limit={limit}
                        total={totalData}
                        handlePageClick={handlePageClick}
                        totalPages={totalPages}
                        marginPagesDisplayed={1}
                      />
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default ChargingSessionForm;